import { ApiError, errorView } from "@nodus/api";
import { Button, EmptyState } from "@nodus/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { AuthCard } from "../../../app/auth-card";
import { useRuntime } from "../../../app/runtime";
import { rememberScope } from "../../../app/scope";
import { useAuth } from "../../../auth/auth-context";
import { InviteCard } from "../../../features/console/invite-card";

export const Route = createFileRoute("/_authed/invite/$token")({ component: InviteByToken });

function InviteByToken() {
  const { token } = Route.useParams();
  const { api } = useRuntime();
  const { session } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invite = useQuery({ queryKey: ["user", "invite", token], queryFn: () => api.lookupInvite(token), retry: false });
  if (invite.isLoading) return <AuthCard title="Opening your invite…" />;
  if (invite.error && !(invite.error instanceof ApiError && [404, 410].includes(invite.error.code))) {
    return (
      <AuthCard title="Couldn't open your invite">
        <p role="alert" className="text-ui text-danger-fg">{errorView(invite.error).title}</p>
        <Button className="mt-6" disabled={invite.isFetching} onClick={() => void invite.refetch()}>
          {invite.isFetching ? "Retrying…" : "Try again"}
        </Button>
      </AuthCard>
    );
  }
  if (!invite.data) {
    return (
      <AuthCard title="This invite has expired">
        <EmptyState title="Ask for a new invite" description="An invite link works once, for 72 hours, and stops working if an Owner or Admin revokes it or resends a new one." />
      </AuthCard>
    );
  }
  return (
    <InviteCard
      invite={invite.data}
      onAccept={async () => {
        const r = await api.acceptInvite({ token });
        await qc.invalidateQueries({ queryKey: ["user"] });
        if (session) rememberScope(session.user.id, r.org, invite.data.projects?.[0] ?? "default");
        void navigate({ to: "/$org", params: { org: r.org } });
      }}
    />
  );
}
